// Frontend registry for the WORLD-LEVEL master catalogs (Phase 2, decisions
// log #5). Mirrors Backend/config/masterCatalogs.js — the backend registry is
// the authority on what may be stored; this file only adds UI metadata (labels,
// input types, enum options) so ONE generic page and ONE pick modal can serve
// both kinds. Update BOTH files together, like accessControls.
//
// No medicine kind (owner, 10 Oct 2026): there is no medicine master. Every
// doctor and every pharmacy builds its own list of medicines and products.
//
// Enum option lists must match the tenant models' ENUM columns exactly — the
// backend refuses anything else (BAD_ENUM_VALUE), because a pick copies these
// values verbatim into ENUM-typed columns.

export const MASTER_CATALOG_KINDS = {
  lab_test: {
    label: 'Lab Test',
    labelPlural: 'Lab Tests',
    idField: 'masterTestId',
    tenantFeature: 'lab',
    subtitleFields: ['code', 'sampleType', 'method'],
    searchHint: 'Search by name, code or LOINC…',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Thyroid Stimulating Hormone' },
      { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'e.g. TSH' },
      { key: 'shortName', label: 'Short name', type: 'text' },
      { key: 'sampleType', label: 'Sample type', type: 'select', options: ['blood', 'serum', 'plasma', 'urine', 'stool', 'swab', 'tissue', 'fluid', 'other'] },
      { key: 'containerType', label: 'Container', type: 'text', placeholder: 'e.g. EDTA 2ml' },
      { key: 'containerColour', label: 'Cap colour', type: 'text', placeholder: 'e.g. lavender' },
      { key: 'method', label: 'Method', type: 'text', placeholder: 'e.g. CLIA' },
      { key: 'unit', label: 'Unit', type: 'text', placeholder: 'e.g. mIU/L' },
      { key: 'loincCode', label: 'LOINC code', type: 'text' },
      { key: 'tatHours', label: 'TAT (hours)', type: 'number', min: 1 },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
  },
  imaging_procedure: {
    label: 'Radiology Procedure',
    labelPlural: 'Radiology Procedures',
    idField: 'masterProcedureId',
    tenantFeature: 'radiology',
    subtitleFields: ['code', 'modality', 'bodyPart'],
    searchHint: 'Search by name or code…',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Chest X-Ray PA' },
      { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'e.g. XR-CHEST' },
      { key: 'modality', label: 'Modality', type: 'select', required: true, options: ['xray', 'ultrasound', 'ct', 'mri', 'mammography', 'dexa', 'echo', 'ecg', 'other'] },
      { key: 'bodyPart', label: 'Body part', type: 'text', placeholder: 'e.g. Chest' },
      { key: 'durationMins', label: 'Duration (mins)', type: 'number', min: 1 },
      { key: 'requiresContrast', label: 'Requires contrast', type: 'checkbox' },
      { key: 'preparation', label: 'Patient preparation', type: 'textarea' },
      { key: 'isPndt', label: 'PC-PNDT regulated', type: 'checkbox' },
      { key: 'radiationDose', label: 'Radiation dose', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
  },
};

export const isMasterCatalogKind = (k) =>
  Object.prototype.hasOwnProperty.call(MASTER_CATALOG_KINDS, k);

/** Empty form state for a kind (checkboxes false, everything else ''). */
export const emptyCatalogForm = (kind) => {
  const out = {};
  for (const f of MASTER_CATALOG_KINDS[kind].fields) {
    out[f.key] = f.type === 'checkbox' ? false : '';
  }
  return out;
};

/** Card subtitle text: the kind's subtitle fields that have values, joined. */
export const catalogSubtitle = (kind, item) =>
  MASTER_CATALOG_KINDS[kind].subtitleFields
    .map((f) => item?.[f])
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
    .join(' · ');
